import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styles from './LandingPage.module.css';
import api from '../../services/api.js'; 
import { API_BASE_URL } from '../../config';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Loader from '../../components/Loader';
import showToast from '../../components/ShowToast';

import { FaHome, FaBriefcase, FaQuoteLeft, FaBuilding, FaEnvelope, FaCheckCircle, FaUser, FaLock, FaMapPin, FaPhone } from 'react-icons/fa';
import kitchenImage from '../../assets/images/resi02.jpeg';
import imageLogo from "../../assets/images/LogoBuildStart03.png";


// --- 2. Role Selection Page Component (No changes here, it's correct) ---
export const RoleSelectPage = () => {
    return (
        <div className={styles.roleSelectContainer}>

          <a href="/" className={styles.authLogo}>
                    <img
            src={imageLogo}   // Replace with your logo image path
            alt="Build Start Logo"
            style={{ height: '35px', marginRight: '10px' }}  // Adjust size & spacing as needed
          />
            
            BUILD START</a>
            <h1 className={styles.authTitle}>Join Build Start</h1>
            <p className={styles.authSubtitle}>First, tell us who you are. This will help us tailor your experience.</p>

            <div className={styles.roleCardsContainer}>
                <a href="/register/homeowner" className={styles.roleCard}>
                    <FaHome className={styles.roleIcon} />
                    <h2>I'm a Homeowner</h2>
                    <p>Hiring professionals for a home project.</p>
                </a>
                <a href="/register/business" className={styles.roleCard}>
                    <FaBriefcase className={styles.roleIcon} />
                    <h2>I'm a Consultant, Trade or Supplier</h2>
                    <p>Looking for clients and to grow my business.</p>
                </a>
                <a href="/register/developer" className={styles.roleCard}>
                    <FaBuilding className={styles.roleIcon} />
                    <h2>I'm a Renovator / Developer</h2>
                    <p>Sourcing subcontractors for large-scale projects.</p>
                </a>

            </div>
             <p className={styles.authRedirect}>
                Already have an account? <a href="/login" className={styles.link}>Log In</a>
            </p>
        </div>
    );
};


// --- UPDATED Registration Page ---
export const RegistrationPage = ({ role }) => {
  // New flags to manage roles
  const isBusiness = role === 'business';
  const isDeveloper = role === 'developer';

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');

  // Updated initial form data to include the developer field
  const initialFormData = {
    businessname: '', // For Business role
    developerbusinessname: '', // For Developer role
    email: '',
    pwd: '',
    confirmpwd: '',
    phone: '',
    firstname: '',
    lastname: '',
    postcode: '',
    suburb: ''
  };

  const [formData, setFormData] = useState(initialFormData);

  const validate = () => {
    const newErrors = {};
    // Updated validation to check for the correct business name based on role
    if (isBusiness && !formData.businessname) newErrors.businessname = 'Business name is required';
    if (isDeveloper && !formData.developerbusinessname) newErrors.developerbusinessname = 'Business name is required';

    // --- All other validation remains the same ---
    if (!formData.firstname) newErrors.firstname = 'First name is required';
    if (!formData.lastname) newErrors.lastname = 'Last name is required';
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email address is invalid';
    }
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[A-Za-z\d@$!%*?&]{8,}$/;
    if (!formData.pwd) {
      newErrors.pwd = 'Password is required';
    } else if (!passwordRegex.test(formData.pwd)) {
      newErrors.pwd = 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one number';
    }
    if (formData.pwd !== formData.confirmpwd) newErrors.confirmpwd = 'Passwords do not match';
    if (!formData.postcode) newErrors.postcode = 'Postcode is required';
    if (!formData.phone) {
        newErrors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone)) {
        newErrors.phone = 'Phone number is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    if (name === 'postcode' && !/^\d{0,4}$/.test(value)) return;
    if (name === 'phone' && !/^\d{0,10}$/.test(value)) return;
    
    setFormData(prev => ({ ...prev, [name]: processedValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      showToast('error', 'Please fix the errors before submitting.');
      return;
    }
    setLoading(true);
    setServerError('');

    // Endpoint logic is correct: Business uses its own, Homeowner and Developer use the same one.
    const endpoint = isBusiness ? '/api/RegisterBusinessUser' : '/api/RegisterHomeUser';
    
    // Updated payload to conditionally add the correct business name field
    const payload = {
      email: formData.email,
      pwd: formData.pwd,
      confirmPwd: formData.confirmpwd,
      firstName: formData.firstname,
      lastName: formData.lastname,
      postcode: formData.postcode,
      phone: formData.phone,
      suburb: formData.suburb,
      ...(isBusiness && { businessname: formData.businessname }),
      ...(isDeveloper && { developerBusinessName: formData.developerbusinessname }) // Use camelCase for JSON
    };

    try {
      const response = await api.post(endpoint, payload);

      Swal.fire({
        title: "Registration Successful!",
        text: response.message || "Please check your email to activate your account.",
        icon: "success",
        confirmButtonText: "Log In"
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/login'); // Navigate all successful registrations to the login page
        }
      });

    } catch (error) {
      let friendlyErrorMessage = 'An unknown error occurred. Please try again.';
      if (error.response) {
        const responseData = error.response.data;
        if (responseData && responseData.errors) {
          friendlyErrorMessage = Object.values(responseData.errors).flat().join(' ');
        } else if (responseData && responseData.message) {
          friendlyErrorMessage = Array.isArray(responseData.message) 
            ? responseData.message.join(' ') 
            : responseData.message;
        }
      } else if (error.request) {
        friendlyErrorMessage = 'Could not connect to the server. Please check your internet connection.';
      }
      setServerError(friendlyErrorMessage);
      showToast('error', 'Registration failed. Please see the error message.');
    } finally {
      setLoading(false);
    }
  };

  // Updated testimonial logic to include a case for the developer role
  const getTestimonial = () => {
    if (isBusiness) {
        return {
            quote: "Joining Build Start was the single best decision for my business. We've seen a 40% increase in qualified leads and can now focus on doing great work instead of chasing clients.",
            author: "Mark C.",
            authorDetail: "Owner, Cornerstone Plumbing",
        };
    }
    if (isDeveloper) {
        return {
            quote: "Build Start has become our go-to for sourcing reliable trade crews for our subdivisions. The quality of professionals is unmatched and saves us weeks in procurement.",
            author: "David Chen",
            authorDetail: "Project Manager, Apex Developments",
        };
    }
    // Default to homeowner
    return {
        quote: "Finding a trustworthy electrician used to be a nightmare. With Build Start, I connected with a 5-star professional in minutes and our project was completed flawlessly. I'll never go back!",
        author: "Sarah L.",
        authorDetail: "Homeowner, Austin, TX",
    };
  };
  const testimonial = getTestimonial();

  const renderFormField = (name, label, type = 'text', IconComponent, required = true) => (
    <div className={styles.formGroup}>
      <label htmlFor={name}>{label}</label>
      <div className={styles.formBox}>

        <input
          type={type}
          id={name}
          name={name}
          value={formData[name]}
          onChange={handleChange}
          required={required}
          className={styles.formInput}
        />
      </div>
      {errors[name] && <p className={styles.errorText}>{errors[name]}</p>}
    </div>
  );

  // Function to get the correct title based on the role
  const getTitle = () => {
      if (isBusiness) return 'Create your Business Account';
      if (isDeveloper) return 'Create your Developer Account';
      return 'Create your Homeowner Account';
  }

  return (
    <div className={styles.authContainer}>
      {loading && <Loader />}
      <div className={`${styles.authVisual} ${styles.testimonialVisual}`}>
        <div className={styles.testimonialContainer}>
            <FaQuoteLeft className={styles.testimonialQuoteIcon} />
            <p className={styles.testimonialText}>"{testimonial.quote}"</p>
            <div className={styles.testimonialAuthor}>
                <div>
                    <strong>{testimonial.author}</strong>
                    <span>{testimonial.authorDetail}</span>
                </div>
            </div>
        </div>
      </div>
    
      <div className={styles.authFormContainer}>
        <div className={styles.authForm}>
          
            <a href="/" className={styles.authLogo}>
                    <img
            src={imageLogo}   // Replace with your logo image path
            alt="Build Start Logo"
            style={{ height: '35px', marginRight: '10px' }}  // Adjust size & spacing as needed
          />
            
            BUILD START</a>


          <h1 className={styles.authTitle}>{getTitle()}</h1>
          <p className={styles.authSubtitle}>Join a community of top-tier professionals and clients.</p>
          
          <form onSubmit={handleSubmit}>
            {/* Conditionally render the correct business name field based on role */}
            {isBusiness && renderFormField('businessname', 'Business Name', 'text', FaBriefcase)}
            {isDeveloper && renderFormField('developerbusinessname', 'Business Name', 'text', FaBuilding)}
            
            <div className={styles.formRow}>
              {renderFormField('firstname', 'First Name', 'text', FaUser)}
              {renderFormField('lastname', 'Last Name', 'text', FaUser)}
            </div>
            <div className={styles.formRow}>
              {renderFormField('postcode', 'Post Code', 'text', FaMapPin)}
              {renderFormField('phone', 'Phone Number', 'text', FaPhone)}
            </div>
            {renderFormField('email', 'Your Email', 'email', FaEnvelope)}
            {renderFormField('pwd', 'Password', 'password', FaLock)}
            {renderFormField('confirmpwd', 'Repeat Your Password', 'password', FaLock)}
            
            {serverError && (
              <div className={styles.serverErrorContainer}>
                <p className={styles.errorText}>{serverError}</p>
              </div>
            )}
            <div className={styles.terms}>
                <input type="checkbox" id="terms" name="terms" required />
                <label htmlFor="terms">I agree to the <a href="/terms" className={styles.link}>Terms of Service</a> and <a href="/privacy" className={styles.link}>Privacy Policy</a>.</label>
            </div>
            <button type="submit" className={styles.btnPrimaryAuth}>Create Account</button>
          </form>
          
          <p className={styles.authRedirect}>
            Already have an account? <a href="/login" className={styles.link}>Log In</a>
          </p>
        </div>
      </div>
    </div>
  );
};


// --- ResetPasswordPage component remains unchanged ---
export const ResetPasswordPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    // State to determine which view to show
    const [token, setToken] = useState(null);
    const [emailFromQuery, setEmailFromQuery] = useState(null);

    // This effect checks the URL for a token on page load
    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenFromQuery = queryParams.get('token');
        const email = queryParams.get('email');
        if (tokenFromQuery && email) {
            setToken(tokenFromQuery);
            setEmailFromQuery(email);
        }
    }, [location]);

    // --- VIEW 1: FORM TO REQUEST THE RESET LINK ---
    const RequestFormView = () => {
        const [email, setEmail] = useState('');
        const [submitted, setSubmitted] = useState(false);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');
            setLoading(true);
            try {
                // IDEAL: A single backend endpoint that handles both user types
                await api.post(`/api/auth/request-password-reset`, { email });
                setLoading(false);
                setSubmitted(true); // Switch to confirmation view
            } catch (err) {
                setLoading(false);
                setError(err.response?.data?.message || 'An error occurred. Please try again.');
            }
        };

        if (submitted) {
            return (
                <div className={styles.authForm}>
                    <div className={styles.confirmationContainer}>
                        <FaEnvelope className={styles.confirmationIcon} />
                        <h1 className={styles.authTitle}>Check your email</h1>
                        <p className={styles.authSubtitle}>
                            We've sent a password reset link to <strong>{email}</strong> if an account with that email exists.
                        </p>
                        <Link to="/login" className={`${styles.btnPrimaryAuth} ${styles.linkButton}`}>Back to Login</Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.authForm}>
                
              <a href="/" className={styles.authLogo}>
                    <img
            src={imageLogo}   // Replace with your logo image path
            alt="Build Start Logo"
            style={{ height: '35px', marginRight: '10px' }}  // Adjust size & spacing as needed
          />
            
            BUILD START</a>

                <h1 className={styles.authTitle}>Forgot Password?</h1>
                <p className={styles.authSubtitle}>Enter your email and we'll send a link to get back into your account.</p>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label htmlFor="email">Email Address</label>
                        <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={styles.formInput} placeholder="you@example.com" />
                         {error && <p className={styles.errorText}>{error}</p>}
                    </div>
                    <button type="submit" className={styles.btnPrimaryAuth} disabled={loading}>
                        {loading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                </form>
                <p className={styles.authRedirect}><Link to="/login" className={styles.link}>← Back to Login</Link></p>
            </div>
        );
    };

    // --- VIEW 2: FORM TO SET THE NEW PASSWORD ---
    const ResetFormView = () => {
        const [password, setPassword] = useState('');
        const [confirmPassword, setConfirmPassword] = useState('');
        const [error, setError] = useState('');
        const [loading, setLoading] = useState(false);
        const [success, setSuccess] = useState(false);

        const validatePassword = (pwd) => {
            // Your existing strong password regex
            const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;
            return passwordRegex.test(pwd);
        };

        const handleSubmit = async (e) => {
            e.preventDefault();
            setError('');

            if (!validatePassword(password)) {
                setError('Password must be at least 8 characters and include uppercase, lowercase, number, and special character.');
                return;
            }
            if (password !== confirmPassword) {
                setError('Passwords do not match.');
                return;
            }
            
            setLoading(true);
            try {
                // IDEAL: A single backend endpoint for resetting
                await api.post(`/api/auth/reset-password`, {
                    email: emailFromQuery,
                    token: token,
                    newPassword: password,
                });
                setLoading(false);
                setSuccess(true); // Switch to success confirmation view
            } catch (err) {
                setLoading(false);
                setError(err.response?.data?.message || 'Invalid or expired token. Please try again.');
            }
        };

        if (success) {
             return (
                <div className={styles.authForm}>
                    <div className={styles.confirmationContainer}>
                        <FaCheckCircle className={styles.confirmationIcon} />
                        <h1 className={styles.authTitle}>Password Reset!</h1>
                        <p className={styles.authSubtitle}>Your password has been changed successfully.</p>
                        <Link to="/login" className={`${styles.btnPrimaryAuth} ${styles.linkButton}`}>Proceed to Login</Link>
                    </div>
                </div>
            );
        }

        return (
            <div className={styles.authForm}>
                <Link to="/" className={styles.authLogo}>Build Start</Link>
                <h1 className={styles.authTitle}>Create New Password</h1>
                <p className={styles.authSubtitle}>Your new password must be different from previous ones.</p>
                <form onSubmit={handleSubmit}>
                    <div className={styles.formGroup}>
                        <label htmlFor="password">New Password</label>
                        <input type="password" id="password" value={password} onChange={(e) => setPassword(e.target.value)} required className={styles.formInput} placeholder="Enter new password" />
                    </div>
                    <div className={styles.formGroup}>
                        <label htmlFor="confirmPassword">Confirm New Password</label>
                        <input type="password" id="confirmPassword" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required className={styles.formInput} placeholder="Confirm new password" />
                    </div>
                    {error && <p className={styles.errorText}>{error}</p>}
                    <button type="submit" className={styles.btnPrimaryAuth} disabled={loading}>
                        {loading ? 'Resetting...' : 'Reset Password'}
                    </button>
                </form>
            </div>
        );
    };

    return (
        <div className={styles.authContainer}>
            <div className={styles.authVisual}>
                {/* A static, calming image is best for the entire reset process */}
                <img src={kitchenImage} alt="Calm and modern kitchen" />
            </div>
            <div className={styles.authFormContainer}>
                {/* Conditionally render the correct view based on the URL token */}
                {token ? <ResetFormView /> : <RequestFormView />}
            </div>
        </div>
    );
};